import React, { useEffect } from 'react';
import {
  InfoCircle,
  StatusAlert,
  StatusComplete,
  StatusWarning,
} from '@wix/wix-ui-icons-common';

import { Text } from '../Text';
import { Button } from '../Button';
import { TextButton } from '../TextButton';
import { CloseButton } from '../CloseButton';
import { SKIN, dataHooks, LAYOUT, SIZE, BORDER } from './constants';
import { classes, st } from './SectionHelper.st.css';
import { SectionHelperSkin, SectionHelperProps } from './SectionHelper.types';
import { deprecationLog } from '../utils/deprecationLog';
import { useIcons } from '../WixDesignSystemIconThemeProvider';

const SectionHelper = ({
  dataHook,
  showCloseButton = false,
  onClose,
  onAction,
  actionText,
  actionDisabled = false,
  secondaryActionProps,
  appearance = SKIN.Warning, // TODO: add default to skin prop once appearance is removed
  title,
  size = SIZE.Medium,
  fullWidth = false,
  children,
  layout = LAYOUT.Vertical,
  border = BORDER.Standard,
  showPrefixIcon = false,
  prefixIcon,
  ...props
}: SectionHelperProps) => {
  const icons = useIcons('SectionHelper', {
    InfoCircle,
    StatusAlert,
    StatusComplete,
    StatusWarning,
  });

  const DEFAULT_ICONS: Record<SectionHelperSkin, React.ReactNode> = {
    [SKIN.Standard]: <icons.InfoCircle />,
    [SKIN.Success]: <icons.StatusComplete />,
    [SKIN.Warning]: <icons.StatusWarning />,
    [SKIN.Danger]: <icons.StatusAlert />,
    [SKIN.Premium]: <icons.InfoCircle />,
    [SKIN.Preview]: <icons.InfoCircle />,
    [SKIN.ExperimentalDark]: <icons.InfoCircle />,
  };

  const skin = props.skin || appearance;
  const isExperimentalDark = skin === SKIN.ExperimentalDark;
  const icon = showPrefixIcon && (prefixIcon ?? DEFAULT_ICONS[skin]);

  useEffect(() => {
    if (appearance) {
      deprecationLog(
        '<SectionHelper/> - prop "appearance" is deprecated and will be removed in next major release, please use "skin" property instead.',
      );
    }
  }, [appearance]);

  return (
    <div
      data-hook={dataHook}
      data-skin={skin}
      className={st(classes.root, {
        skin,
        withCloseBtn: showCloseButton,
        fullWidth,
        size,
        border,
        hasPrefix: showPrefixIcon,
      })}
    >
      {showCloseButton && (
        <div className={st(classes.close, { size })}>
          <CloseButton
            dataHook={dataHooks.closeButton}
            size="medium"
            skin={isExperimentalDark ? 'light' : 'dark'}
            onClick={onClose}
          />
        </div>
      )}

      {icon && (
        <Text
          dataHook={dataHooks.prefix}
          className={st(classes.prefix)}
          light={isExperimentalDark}
        >
          {icon}
        </Text>
      )}

      <div className={st(classes.content, { size, layout })}>
        <div className={st(classes.textContainer)}>
          {title && (
            <div className={classes.title}>
              <Text
                className={classes.text}
                light={isExperimentalDark}
                dataHook={dataHooks.title}
                size="small"
                weight="bold"
              >
                {title}
              </Text>
            </div>
          )}

          <Text
            className={classes.text}
            size="small"
            light={isExperimentalDark}
          >
            {children}
          </Text>
        </div>

        {(actionText || secondaryActionProps) && (
          <div className={st(classes.actions, { size, layout })}>
            {actionText && (
              <Button
                className={st(classes.action)}
                size={size === 'small' ? 'tiny' : 'small'}
                skin={isExperimentalDark ? 'standard' : 'dark'}
                priority={isExperimentalDark ? 'primary' : 'secondary'}
                disabled={actionDisabled}
                onClick={onAction}
                dataHook={dataHooks.actionButton}
              >
                {actionText}
              </Button>
            )}

            {secondaryActionProps && (
              <TextButton
                className={st(classes.secondaryAction)}
                size="small"
                skin={isExperimentalDark ? 'light' : 'dark'}
                onClick={secondaryActionProps.onClick}
                dataHook={dataHooks.secondaryActionButton}
              >
                {secondaryActionProps.label}
              </TextButton>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

SectionHelper.displayName = 'SectionHelper';

export { SectionHelper };
